import { useEffect } from 'react';
import { L } from '../../../utils/logger';
import type { TransportClock } from '../../../scheduler/clock';
import { mediaTimeAt } from '../../../scheduler/clock';

interface TickerArgs {
  tracks: Array<{ role: string; url: string }>;
  refs: any;
  status: 'idle' | 'loading' | 'playing' | 'paused';
  masterPlaybackRate: number;
  duration: number;
  bufferModeRef: React.MutableRefObject<boolean>;
  logicalStartRef: React.MutableRefObject<number>;
  startedAtRef: React.MutableRefObject<number>;
  seekingRef: React.MutableRefObject<{ runId: number; target: number } | null>;
  setCurrentTime: (n: number) => void;
}

export function useTimeTicker({ tracks, refs, status, masterPlaybackRate, duration, bufferModeRef, logicalStartRef, startedAtRef, seekingRef, setCurrentTime }: TickerArgs) {
  // Anchor only on play/rate-change elsewhere; ticker does not re-anchor per frame
  // (Composer/useSpeedDsp manage anchoring on play/seek/rate-change.)

  useEffect(() => {
    let raf: number | null = null;
  const logTick = L.throttled('media:transport', 'tick', 1000);
    const tick = () => {
      const seeking = seekingRef.current;
      if (seeking) {
        setCurrentTime(seeking.target);
      } else if (bufferModeRef.current && refs.audioCtxRef.current) {
        const ctx = refs.audioCtxRef.current;
        // Ensure a stable transport clock exists; create if missing
  const clkRef = (refs as any).transportClockRef || ((refs as any).transportClockRef = { current: null });
  const clk: TransportClock | null = clkRef.current;
        const t = status === 'playing'
          ? (clk ? mediaTimeAt(clk, ctx.currentTime) : (logicalStartRef.current + (ctx.currentTime - startedAtRef.current) * masterPlaybackRate))
          : logicalStartRef.current;
        const dur = duration || Infinity;
        setCurrentTime(Math.max(0, Math.min(dur, t)));

        // Optional UI vs media drift probe (throttled)
        try {
          const clkNow = (refs as any).transportClockRef?.current as TransportClock | null | undefined;
          const mediaNow = clkNow ? mediaTimeAt(clkNow, ctx.currentTime) : null;
          logTick('debug', 'tick', { ctxNow: ctx.currentTime, uiTime: t, mediaNow, rate: masterPlaybackRate });
        } catch {}
      } else {
        // Element mode: choose the largest currentTime among stems (handles drift & silence stems)
        let maxT = 0; let found = false;
        for (const tr of tracks) {
          const el = refs.elsRef.current[tr.role];
            if (el && !Number.isNaN(el.currentTime)) { if (el.currentTime > maxT) maxT = el.currentTime; found = true; }
        }
        if (found) {
          const dur = duration || Infinity;
          setCurrentTime(Math.max(0, Math.min(dur, maxT)));
        }
      }
      if (status === 'playing') raf = requestAnimationFrame(tick);
    };
    if (status === 'playing') raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf!); };
  }, [tracks, status, masterPlaybackRate, duration]);
}
